/**
 * OTA Command Center — UI shell for #/ota (superadmin).
 * Live data from GET /ota/campaigns — no hardcoded fleet demo (INC-OS-FE-002).
 */

import { escapeHtml } from "./dom.js";

function campaignCounters(camp) {
  const raw = camp && camp.counters && typeof camp.counters === "object" ? camp.counters : {};
  const success = Number(raw.success || raw.completed || 0);
  const failed = Number(raw.failed || 0);
  const updating = Number(raw.updating || raw.in_progress || 0);
  const pending = Number(raw.pending || raw.queued || 0);
  const total = success + failed + updating + pending;
  const pct = total > 0 ? Math.round((success / total) * 100) : 0;
  return { success, failed, updating, pending, offline: 0, total, pct };
}

function aggregateCounters(campaigns) {
  const rows = Array.isArray(campaigns) ? campaigns : [];
  const out = { success: 0, failed: 0, updating: 0, pending: 0, offline: 0, total: 0 };
  for (const c of rows) {
    const n = campaignCounters(c);
    out.success += n.success;
    out.failed += n.failed;
    out.updating += n.updating;
    out.pending += n.pending;
    out.total += n.total;
  }
  return out;
}

function stateToUiStatus(state) {
  const s = String(state || "").toLowerCase();
  if (s.includes("fail") || s === "declined") return "failed";
  if (s.includes("roll")) return "failed";
  if (s.includes("accept") || s === "active" || s === "running") return "updating";
  if (s === "pending" || s === "draft") return "pending";
  if (s.includes("complete") || s === "done") return "success";
  return "pending";
}

function zoneStateClass(state) {
  if (state === "success") return "success";
  if (state === "updating") return "updating";
  if (state === "pending") return "pending";
  if (state === "failed") return "failed";
  return "pending";
}

function formatCampaignTs(ts) {
  const t = Number(ts);
  if (!Number.isFinite(t) || t <= 0) return "—";
  try {
    return new Date(t * 1000).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

/**
 * @param {unknown[]} campaigns
 */
export function buildOtaCcViewFromCampaigns(campaigns) {
  const rows = (Array.isArray(campaigns) ? campaigns : [])
    .slice()
    .sort((a, b) => (Number(b.updated_at) || 0) - (Number(a.updated_at) || 0));
  const latest = rows[0] || null;
  const summary = aggregateCounters(rows);
  const feed = rows.slice(0, 8).map((c) => {
    const st = stateToUiStatus(c.state);
    const ctr = campaignCounters(c);
    return {
      id: String(c.id || ""),
      status: st,
      title: `Campaign ${String(c.id || "").slice(0, 8)}`,
      meta: `v${String(c.fw_version || "—")} · ${String(c.state || "—")} · ${ctr.total} device runs`,
      time: formatCampaignTs(c.updated_at || c.created_at),
    };
  });
  const timeline = [];
  for (const c of rows.slice(0, 5)) {
    const decisions = Array.isArray(c.decisions) ? c.decisions : [];
    for (const d of decisions.slice(0, 4)) {
      timeline.push({
        t: formatCampaignTs(d.decided_at),
        label: `${String(c.id || "").slice(0, 8)} · ${String(d.admin_username || "—")} · ${String(d.action || "—")}`,
        ok: !String(d.action || "").toLowerCase().includes("declin"),
      });
    }
  }
  timeline.sort((a, b) => String(b.t).localeCompare(String(a.t)));
  const groups = new Map();
  for (const c of rows) {
    const key = String(c.fw_version || "unknown").trim() || "unknown";
    const g = groups.get(key) || { id: key, name: `Firmware v${key}`, count: 0, status: stateToUiStatus(c.state) };
    g.count += 1;
    groups.set(key, g);
  }
  const deviceGroups = [...groups.values()];
  const snapshots = rows.slice(0, 4).map((c) => ({
    id: String(c.id || ""),
    status: stateToUiStatus(c.state),
    label: String(c.fw_version || c.id || "—"),
  }));
  let currentDeployment = null;
  if (latest) {
    const ctr = campaignCounters(latest);
    const targets = Array.isArray(latest.target_admins) ? latest.target_admins.join(", ") : "—";
    currentDeployment = {
      name: `Campaign ${String(latest.id || "")}`,
      target: targets,
      version: String(latest.fw_version || "—"),
      progress: ctr.pct,
      success: ctr.success,
      failed: ctr.failed,
      pending: ctr.pending,
    };
  }
  const riskRows = latest
    ? [
        { label: "Campaign state", value: String(latest.state || "—"), ok: stateToUiStatus(latest.state) !== "failed" },
        { label: "Firmware version", value: String(latest.fw_version || "—"), ok: !!latest.fw_version },
        { label: "Device runs tracked", value: String(campaignCounters(latest).total), ok: campaignCounters(latest).total > 0 },
        { label: "SHA-256 catalog", value: latest.sha256 ? "Present" : "—", ok: !!latest.sha256 },
      ]
    : [];
  return {
    campaigns: rows,
    summary,
    feed,
    timeline: timeline.slice(0, 12),
    deviceGroups,
    snapshots,
    currentDeployment,
    riskRows,
    riskScore: latest && campaignCounters(latest).total > 0 ? campaignCounters(latest).pct : null,
  };
}

function buildCampaignMapCards(campaigns) {
  const rows = Array.isArray(campaigns) ? campaigns : [];
  if (!rows.length) {
    return '<p class="muted croc-cc-empty">No campaigns yet — create one from Actions or open Deployments.</p>';
  }
  return rows.slice(0, 8).map((c, idx) => {
    const ctr = campaignCounters(c);
    const st = stateToUiStatus(c.state);
    const x = 12 + (idx % 4) * 22;
    const y = 16 + Math.floor(idx / 4) * 38;
    return `
    <article class="croc-cc-ota__zone-card" data-ota-campaign="${escapeHtml(String(c.id || ""))}" style="left:${x}%;top:${y}%">
      <div class="croc-cc-ota__zone-head">
        <span class="croc-cc-ota__zone-ico croc-cc-ota__zone-ico--blue" aria-hidden="true">↑</span>
        <span class="croc-cc-ota__zone-name">${escapeHtml(String(c.fw_version || c.id || "Campaign"))}</span>
      </div>
      <p class="croc-cc-ota__zone-meta">${escapeHtml(String(c.state || "—"))} · ${ctr.total} runs</p>
      <div class="croc-cc-ota__zone-progress"><i style="width:${ctr.pct}%"></i></div>
      <p class="croc-cc-ota__zone-pct">${ctr.pct}% · ${escapeHtml(st)}</p>
    </article>`;
  }).join("");
}

function buildDeviceGroups(groups) {
  const list = Array.isArray(groups) ? groups : [];
  if (!list.length) {
    return '<p class="muted croc-cc-empty">No firmware groups until campaigns exist.</p>';
  }
  return list.map((g) => `
    <div class="croc-cc-ota__group" data-ota-group="${escapeHtml(g.id)}">
      <span class="croc-cc-ota__group-ico" aria-hidden="true">◎</span>
      <span class="croc-cc-ota__group-body">
        <span class="croc-cc-ota__group-name">${escapeHtml(g.name)}</span>
        <span class="croc-cc-ota__group-meta">${g.count} campaign(s)</span>
      </span>
      <span class="croc-cc-ota__group-dot croc-cc-ota__group-dot--${zoneStateClass(g.status)}" title="${escapeHtml(g.status)}"></span>
    </div>`).join("");
}

function feedGlyph(status) {
  if (status === "failed") return "!";
  if (status === "updating") return "↻";
  if (status === "pending") return "…";
  return "✓";
}

function buildFeedCards(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return '<p class="muted croc-cc-empty">No campaign activity yet.</p>';
  }
  return list.map((f) => `
    <div class="croc-cc-ota__feed-card" data-ota-feed="${escapeHtml(f.id || "")}">
      <span class="croc-cc-ota__feed-ico croc-cc-ota__feed-ico--${zoneStateClass(f.status)}" aria-hidden="true">${feedGlyph(f.status)}</span>
      <span class="croc-cc-ota__feed-body">
        <span class="croc-cc-ota__feed-title">${escapeHtml(f.title)}</span>
        <span class="croc-cc-ota__feed-meta">${escapeHtml(f.meta)}</span>
      </span>
      <span class="croc-cc-ota__feed-time">${escapeHtml(f.time)}</span>
    </div>`).join("");
}

function buildTimelineRows(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) {
    return '<p class="muted croc-cc-empty">Decision timeline appears when admins accept or decline campaigns.</p>';
  }
  return list.map((r) => `
    <div class="croc-cc-ota__timeline-row">
      <span class="croc-cc-ota__timeline-time mono">${escapeHtml(r.t)}</span>
      <span class="croc-cc-ota__timeline-dot ${r.ok ? "croc-cc-ota__timeline-dot--ok" : "croc-cc-ota__timeline-dot--alert"}"></span>
      <span class="croc-cc-ota__timeline-label">${escapeHtml(r.label)}</span>
    </div>`).join("");
}

function buildRiskRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    return '<p class="muted croc-cc-empty">Risk panel fills when a campaign is active.</p>';
  }
  return list.map((r) => `
    <div class="croc-cc-ota__risk-row">
      <span class="croc-cc-ota__risk-label">${escapeHtml(r.label)}</span>
      <span class="croc-cc-ota__risk-value">${escapeHtml(r.value)}</span>
    </div>`).join("");
}

function buildSnapshotCards(snapshots) {
  const list = Array.isArray(snapshots) ? snapshots : [];
  if (!list.length) {
    return '<p class="muted croc-cc-empty">No recent campaigns.</p>';
  }
  return list.map((s) => `
    <div class="croc-cc-ota__snap croc-cc-ota__snap--${zoneStateClass(s.status)}">
      <span>${escapeHtml(String(s.status || "").toUpperCase())}</span>
      <small>${escapeHtml(s.label)}</small>
    </div>`).join("");
}

function buildSummaryGrid(summary = {}) {
  const s = summary || {};
  return `
    <div class="croc-cc-ota__summary-cell croc-cc-ota__summary-cell--success">
      <div class="croc-cc-ota__summary-v">${s.success ?? 0}</div>
      <div class="croc-cc-ota__summary-k">Success</div>
    </div>
    <div class="croc-cc-ota__summary-cell croc-cc-ota__summary-cell--updating">
      <div class="croc-cc-ota__summary-v">${s.updating ?? 0}</div>
      <div class="croc-cc-ota__summary-k">Updating</div>
    </div>
    <div class="croc-cc-ota__summary-cell croc-cc-ota__summary-cell--failed">
      <div class="croc-cc-ota__summary-v">${s.failed ?? 0}</div>
      <div class="croc-cc-ota__summary-k">Failed</div>
    </div>`;
}

function buildDeploymentOverlay(ctx = {}) {
  const current = ctx.currentDeployment;
  if (!current) {
    return `<p class="muted croc-cc-empty" style="margin:12px">Select or create a campaign to see rollout progress.</p>`;
  }
  const total = Math.max(1, Number(current.success || 0) + Number(current.failed || 0) + Number(current.pending || 0));
  const successPct = Math.round((100 * Number(current.success || 0)) / total);
  const failedPct = Math.round((100 * Number(current.failed || 0)) / total);
  const pendingPct = Math.max(0, 100 - successPct - failedPct);
  return `
    <article class="croc-cc-ota__deploy-card">
      <div class="croc-cc-ota__deploy-head">
        <h4 class="croc-cc-ota__deploy-title">${escapeHtml(current.name)}</h4>
        <span class="croc-cc-ota__deploy-badge">LIVE</span>
      </div>
      <div class="croc-cc-ota__deploy-body">
        <div class="croc-cc-ota__deploy-stat">
          <span class="croc-cc-ota__deploy-stat-k">Target Version</span>
          <span class="croc-cc-ota__deploy-stat-v mono">${escapeHtml(current.version)}</span>
        </div>
        <div class="croc-cc-ota__deploy-stat">
          <span class="croc-cc-ota__deploy-stat-k">Targets</span>
          <span class="croc-cc-ota__deploy-stat-v mono">${escapeHtml(current.target)}</span>
        </div>
        <div class="croc-cc-ota__deploy-stat">
          <span class="croc-cc-ota__deploy-stat-k">Success</span>
          <span class="croc-cc-ota__deploy-stat-v mono croc-cc-ota__deploy-stat-v--ok">${current.success}</span>
        </div>
        <div class="croc-cc-ota__deploy-stat">
          <span class="croc-cc-ota__deploy-stat-k">Failed</span>
          <span class="croc-cc-ota__deploy-stat-v mono croc-cc-ota__deploy-stat-v--fail">${current.failed}</span>
        </div>
      </div>
      <div class="croc-cc-ota__deploy-progress" role="img" aria-label="Rollout mix success ${successPct}% pending ${pendingPct}% failed ${failedPct}%">
        <i class="croc-cc-ota__deploy-seg croc-cc-ota__deploy-seg--ok" style="width:${successPct}%"></i>
        <i class="croc-cc-ota__deploy-seg croc-cc-ota__deploy-seg--pending" style="width:${pendingPct}%"></i>
        <i class="croc-cc-ota__deploy-seg croc-cc-ota__deploy-seg--fail" style="width:${failedPct}%"></i>
      </div>
      <div class="croc-cc-ota__deploy-actions">
        <button type="button" class="croc-cc-ota__deploy-btn croc-cc-ota__deploy-btn--primary" id="otaCcViewDetailsBtn">View deployments</button>
      </div>
    </article>`;
}

/**
 * Build the full OTA Command Center content HTML (empty shell — patch live data after mount).
 */
export function buildOtaCommandCenterHtml(_ctx = {}) {
  const emptyVm = buildOtaCcViewFromCampaigns([]);
  return `
<div class="croc-cc-ota" id="crocCcOta" role="main" aria-label="OTA command center">
  <div class="croc-cc-ota__grid">
    <aside class="croc-cc-ota__left">
      <section class="croc-cc-ota__panel">
        <div class="croc-cc-ota__panel-head">
          <h3 class="croc-cc-ota__panel-title">OTA Deployment</h3>
        </div>
        <nav class="croc-cc-ota__dep-nav" aria-label="OTA deployment views">
          <button type="button" class="croc-cc-ota__dep-item croc-cc-ota__dep-item--active" data-ota-tab="map">
            <span class="croc-cc-ota__dep-ico" aria-hidden="true">⌖</span>
            <span>Deployment Map</span>
          </button>
          <button type="button" class="croc-cc-ota__dep-item" data-ota-tab="deployments">
            <span class="croc-cc-ota__dep-ico" aria-hidden="true">⬍</span>
            <span>Deployments</span>
          </button>
        </nav>
      </section>

      <section class="croc-cc-ota__panel">
        <div class="croc-cc-ota__panel-head">
          <h3 class="croc-cc-ota__panel-title">Firmware groups</h3>
          <span class="croc-cc-ota__panel-sub" id="otaCcGroupCount">0</span>
        </div>
        <div class="croc-cc-ota__groups" id="otaCcGroups">${buildDeviceGroups(emptyVm.deviceGroups)}</div>
      </section>

      <section class="croc-cc-ota__panel croc-cc-ota__panel--inset">
        <div class="croc-cc-ota__panel-head">
          <h3 class="croc-cc-ota__panel-title">Deployment Status</h3>
        </div>
        <div class="croc-cc-ota__legend">
          <div class="croc-cc-ota__legend-row croc-cc-ota__legend-row--emphasis"><span><span class="croc-cc-ota__legend-dot croc-cc-ota__legend-dot--success"></span>Success</span><span id="otaCcLegendSuccess">0</span></div>
          <div class="croc-cc-ota__legend-row croc-cc-ota__legend-row--emphasis"><span><span class="croc-cc-ota__legend-dot croc-cc-ota__legend-dot--updating"></span>Updating</span><span id="otaCcLegendUpdating">0</span></div>
          <div class="croc-cc-ota__legend-row"><span><span class="croc-cc-ota__legend-dot croc-cc-ota__legend-dot--pending"></span>Pending</span><span id="otaCcLegendPending">0</span></div>
          <div class="croc-cc-ota__legend-row"><span><span class="croc-cc-ota__legend-dot croc-cc-ota__legend-dot--failed"></span>Failed</span><span id="otaCcLegendFailed">0</span></div>
        </div>
      </section>
    </aside>

    <section class="croc-cc-ota__center" aria-label="OTA deployment map">
      <div class="croc-cc-ota__deployments glass-panel glass-panel--panel" id="otaCcDeploymentsView" hidden>
        <div class="croc-cc-ota__panel-head">
          <div>
            <h3 class="croc-cc-ota__panel-title">Campaign deployments</h3>
            <p class="croc-cc-ota__panel-sub">Accept · decline · rollback lifecycle (SA / target admin)</p>
          </div>
          <button type="button" class="croc-cc-ota__deploy-btn croc-cc-ota__deploy-btn--ghost" id="otaCcDeploymentsRefresh">Refresh</button>
        </div>
        <div id="otaCcDeploymentsTable" class="croc-cc-ota__deployments-table"><p class="muted">Loading campaigns…</p></div>
      </div>
      <div class="croc-cc-ota__map-view" id="otaCcMapView">
      <div class="croc-cc-ota__map-wrap">
        <div class="croc-cc-ota__map-stage">
          <div class="croc-cc-ota__map-bg" aria-hidden="true"></div>
          <div class="croc-cc-ota__map-vignette" aria-hidden="true"></div>
          <div class="croc-cc-ota__map-campaigns" id="otaCcMapCampaigns">${buildCampaignMapCards([])}</div>
          <div id="otaCcDeployOverlay">${buildDeploymentOverlay({})}</div>
        </div>
      </div>
      </div>
    </section>

    <aside class="croc-cc-ota__right">
      <section class="croc-cc-ota__panel">
        <div class="croc-cc-ota__panel-head">
          <div>
            <h3 class="croc-cc-ota__panel-title">OTA Command Feed</h3>
            <p class="croc-cc-ota__panel-sub">Campaign rollout status</p>
          </div>
        </div>
        <div class="croc-cc-ota__feed" id="otaCcFeed">${buildFeedCards([])}</div>
      </section>

      <section class="croc-cc-ota__panel">
        <div class="croc-cc-ota__panel-head">
          <div>
            <h3 class="croc-cc-ota__panel-title">Rollout summary</h3>
            <p class="croc-cc-ota__panel-sub">Aggregated device-run counters</p>
          </div>
        </div>
        <div class="croc-cc-ota__summary-grid" id="otaCcSummary">${buildSummaryGrid(emptyVm.summary)}</div>
      </section>

      <section class="croc-cc-ota__panel croc-cc-ota__panel--inset">
        <div class="croc-cc-ota__panel-head">
          <h3 class="croc-cc-ota__panel-title">Actions</h3>
        </div>
        <div class="croc-cc-ota__actions">
          <button type="button" class="croc-cc-ota__deploy-btn croc-cc-ota__deploy-btn--primary croc-cc-ota__deploy-btn--block" id="otaCcUploadBtn">Upload firmware</button>
          <button type="button" class="croc-cc-ota__deploy-btn croc-cc-ota__deploy-btn--ghost croc-cc-ota__deploy-btn--block" id="otaCcCreateCampaignBtn">Create campaign</button>
          <button type="button" class="croc-cc-ota__deploy-btn croc-cc-ota__deploy-btn--ghost croc-cc-ota__deploy-btn--quiet" id="otaCcRefreshBtn">Refresh</button>
        </div>
      </section>
    </aside>
  </div>

  <footer class="croc-cc-ota__analytics">
    <article class="croc-cc-ota__panel croc-cc-ota__analytic">
      <h4 class="croc-cc-ota__analytic-title">Recent campaigns</h4>
      <div class="croc-cc-ota__snapshots" id="otaCcSnapshots">${buildSnapshotCards([])}</div>
    </article>

    <article class="croc-cc-ota__panel croc-cc-ota__analytic">
      <h4 class="croc-cc-ota__analytic-title">Campaign health</h4>
      <div class="croc-cc-ota__risk">
        <div class="croc-cc-ota__risk-gauge">
          <svg viewBox="0 0 120 64" aria-hidden="true">
            <path class="croc-cc-ota__risk-track" d="M 12 52 A 48 48 0 0 1 108 52"/>
            <path class="croc-cc-ota__risk-fill" id="otaCcRiskFill" d="M 12 52 A 48 48 0 0 1 108 52" style="--ota-risk: 0"/>
          </svg>
          <div class="croc-cc-ota__risk-readout">
            <span class="croc-cc-ota__risk-score mono" id="otaCcRiskScore">—</span>
            <span class="croc-cc-ota__risk-band" id="otaCcRiskBand">No data</span>
          </div>
        </div>
        <div class="croc-cc-ota__risk-list" id="otaCcRisk">${buildRiskRows([])}</div>
      </div>
    </article>

    <article class="croc-cc-ota__panel croc-cc-ota__analytic">
      <h4 class="croc-cc-ota__analytic-title">Decision timeline</h4>
      <div class="croc-cc-ota__timeline" id="otaCcTimeline">${buildTimelineRows([])}</div>
    </article>
  </footer>
</div>`;
}

/**
 * Patch OTA CC panels from GET /ota/campaigns items.
 * @param {ParentNode} view
 * @param {unknown[]} campaigns
 */
export function patchOtaCommandCenterLive(view, campaigns) {
  if (!view) return;
  const vm = buildOtaCcViewFromCampaigns(campaigns);
  const setHtml = (sel, html) => {
    const el = view.querySelector(sel);
    if (el) el.innerHTML = html;
  };
  setHtml("#otaCcMapCampaigns", buildCampaignMapCards(vm.campaigns));
  setHtml("#otaCcDeployOverlay", buildDeploymentOverlay(vm));
  setHtml("#otaCcFeed", buildFeedCards(vm.feed));
  setHtml("#otaCcSummary", buildSummaryGrid(vm.summary));
  setHtml("#otaCcSnapshots", buildSnapshotCards(vm.snapshots));
  setHtml("#otaCcTimeline", buildTimelineRows(vm.timeline));
  setHtml("#otaCcRisk", buildRiskRows(vm.riskRows));
  setHtml("#otaCcGroups", buildDeviceGroups(vm.deviceGroups));
  const gc = view.querySelector("#otaCcGroupCount");
  if (gc) gc.textContent = String(vm.deviceGroups.length);
  patchOtaCommandCenterMetrics(view, vm.summary);
  const scoreEl = view.querySelector("#otaCcRiskScore");
  const bandEl = view.querySelector("#otaCcRiskBand");
  const fillEl = view.querySelector("#otaCcRiskFill");
  if (vm.riskScore != null) {
    if (scoreEl) scoreEl.textContent = String(vm.riskScore);
    if (bandEl) bandEl.textContent = vm.riskScore >= 80 ? "Healthy" : vm.riskScore >= 50 ? "Watch" : "At risk";
    if (fillEl) fillEl.style.setProperty("--ota-risk", String(vm.riskScore));
  } else {
    if (scoreEl) scoreEl.textContent = "—";
    if (bandEl) bandEl.textContent = "No data";
    if (fillEl) fillEl.style.setProperty("--ota-risk", "0");
  }
}

export function wireOtaCommandCenter(view, handlers = {}) {
  const on = (sel, evt, fn) => {
    const el = view.querySelector(sel);
    if (el) el.addEventListener(evt, fn);
    return el;
  };

  on("#otaCcUploadBtn", "click", () => {
    if (typeof handlers.onUpload === "function") handlers.onUpload();
  });
  on("#otaCcCreateCampaignBtn", "click", () => {
    if (typeof handlers.onCreateCampaign === "function") handlers.onCreateCampaign();
  });
  on("#otaCcRefreshBtn", "click", () => {
    if (typeof handlers.onRefresh === "function") handlers.onRefresh();
  });
  on("#otaCcViewDetailsBtn", "click", () => {
    if (typeof handlers.onTab === "function") handlers.onTab("deployments");
    const depTab = view.querySelector('[data-ota-tab="deployments"]');
    if (depTab) depTab.click();
  });

  view.querySelectorAll("[data-ota-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      view.querySelectorAll("[data-ota-tab]").forEach((b) => b.classList.remove("croc-cc-ota__dep-item--active"));
      btn.classList.add("croc-cc-ota__dep-item--active");
      if (typeof handlers.onTab === "function") handlers.onTab(btn.getAttribute("data-ota-tab"));
    });
  });

  return () => {};
}

/** Patch dynamic metric text in the OTA CC. */
export function patchOtaCommandCenterMetrics(view, vals = {}) {
  const set = (id, v) => {
    const el = view.querySelector(`#${id}`);
    if (el && v != null && el.textContent !== String(v)) el.textContent = String(v);
  };
  set("otaCcLegendSuccess", vals.success);
  set("otaCcLegendUpdating", vals.updating);
  set("otaCcLegendPending", vals.pending);
  set("otaCcLegendFailed", vals.failed);
}
